///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2021
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import SendIcon from "../../icons/SendIcon";
import Spinner from "../../icons/Spinner";
import SystemMessage from "./SystemMessage";
import UserMessage from "./UserMessage";
import { postUserMessage } from "../../../api/messages";
import setElementScrollTop from "../../../helpers/setElementScrollTop";
import {
  messageType as msgType,
  division,
  dateFormat
} from "../../../helpers/types";
import PanelStateIndicator from "../PanelStateIndicator";
import MessageInput from "./MessageInput";
import { getEmployees } from "../../../api/persons";
import formatMessage from "../../../helpers/formatMessage";
import messages from "../../../helpers/messages";

let container;

function Messages({ loading, error, fetchDetails, bidId, bidMessages }) {
  const { user } = useSelector(state => state.user);
  let _messagesRef = null;

  const [userMessage, setUserMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  useEffect(() => {
    if (bidMessages || errorMessages.length) {
      setElementScrollTop(_messagesRef);
    }
  }, [bidMessages, errorMessages, _messagesRef]);

  const sendUserMessage = useCallback(
    message => {
      if (message) {
        setUserMessage("");
        setIsLoading(true);
        setErrorMessages(errorMessages.filter(x => x !== message));
        postUserMessage({ bidId: parseInt(bidId), message: message })
          .then(() => {
            setIsLoading(false);
            fetchDetails();
          })
          .catch(() => {
            setIsLoading(false);
            let errorMessage = errorMessages.filter(x => x !== message);
            setErrorMessages([...errorMessage, message]);
          });
      }
    },
    [bidId, fetchDetails, errorMessages]
  );

  const deleteMessage = useCallback(
    message => {
      setErrorMessages(errorMessages.filter(x => x !== message));
    },
    [errorMessages]
  );

  const fetchUsers = (searchTerm, callback) => {
    if (!searchTerm || searchTerm.length < 3) return;
    setIsLoading(true);
    getEmployees(searchTerm)
      .then(({ data }) => {
        setIsLoading(false);
        const options = data.map(({ userId, fullName }) => ({
          display: fullName,
          id: userId
        }));
        if ("all".indexOf(searchTerm.toLowerCase()) !== -1) {
          options.unshift({ display: messages.all.defaultMessage, id: -1 });
        }
        return options;
      })
      .catch(() => {
        setIsLoading(false);
        return [];
      })
      .then(callback);
  };

  return (
    <div className="messages card pos-relative">
      <div
        className="border-bottom-grey card__heading--large pos-relative uppercase widget"
        data-testid="messages-widget"
      >
        {messages.messages.defaultMessage}
      </div>

      {(loading || error) && (
        <div className="p-40 box panel-content">
          <PanelStateIndicator
            loading={loading}
            error={error}
            handleRefresh={fetchDetails}
          />
        </div>
      )}

      {bidMessages && (
        <div
          className="scroll mx-8 scroll-on-hover panel-content"
          ref={r => (_messagesRef = r)}
        >
          <div className="one-whole grid" data-testid="messages">
            {bidMessages.length &&
              bidMessages.map((msg, index) => {
                if (msg.messageType === msgType.system) {
                  let systemDateFormat =
                    user.division === division.coy
                      ? dateFormat.MMddyy
                      : dateFormat.ddMMyy;
                  return (
                    <SystemMessage
                      {...msg.systemMessage}
                      data-testid={`system-message-${index}`}
                      key={index}
                      date={format(
                        new Date(msg.createDateUTC),
                        systemDateFormat
                      )}
                    />
                  );
                } else {
                  const {
                    userMessage: {
                      userName,
                      profilePicture,
                      message,
                      isPreviouslySeenByUser
                    },
                    createDateUTC
                  } = msg;
                  let nameInitials = userName
                    .split(" ")
                    .map(x => x.substring(0, 1))
                    .join("");

                  return (
                    <UserMessage
                      key={index}
                      data-testid={`user-message-${index}`}
                      fullName={userName}
                      profilePicture={profilePicture}
                      userMessage={message}
                      createDateUTC={createDateUTC}
                      isPreviouslySeenByUser={isPreviouslySeenByUser}
                      nameInitials={nameInitials}
                      userDivision={user.division}
                    />
                  );
                }
              })}
            {errorMessages.length &&
              errorMessages.map((errorMessage, index) => {
                let userMessage = formatMessage({ message: errorMessage });
                let nameInitials = user.fullName
                  .split(" ")
                  .map(x => x.substring(0, 1))
                  .join("");

                return (
                  <UserMessage
                    key={index}
                    data-testid={`error-message-${index}`}
                    fullName={user.fullName}
                    profilePicture={user.profilePicture}
                    userMessage={userMessage.message}
                    isError={true}
                    nameInitials={nameInitials}
                    deleteMessage={() => deleteMessage(errorMessage)}
                    sendUserMessage={() => sendUserMessage(errorMessage.trim())}
                  />
                );
              })}
          </div>
        </div>
      )}

      <div className="m-24 mb-0 pos-relative grid-textarea-bg p-4 pl-16">
        <MessageInput
          value={userMessage}
          onChange={setUserMessage}
          maxLength={1000}
          fetchUsers={fetchUsers}
          container={container}
        />
        {isLoading ? (
          <Spinner
            fill="#343434"
            className="mr-4 spinner"
            height="16px"
            width="16px"
            style={{ top: 4, right: 8 }}
          />
        ) : (
          <SendIcon
            className={"mr-4 " + (userMessage && "cursor-pointer")}
            fill="#343434"
            height="16px"
            width="16px"
            onClick={() => sendUserMessage(userMessage.trim())}
            onKeyPress={e =>
              e.charCode === 13 && sendUserMessage(userMessage.trim())
            }
            tabIndex={0}
          />
        )}
      </div>
      <div
        ref={el => {
          container = el;
        }}
        className="mb-24 mx-24 pos-relative"
      ></div>
    </div>
  );
}

export default Messages;
